import { yupResolver } from '@hookform/resolvers/yup';
import { capitalizeFirstLetter, cleanFormValues, type FormData, type IUseFormData, type IUseFormDataOptions } from 'BreetHelpers';
import get from 'lodash/get';
import { useCallback, useEffect } from 'react';
import { type FieldError, type FieldPath, type PathValue, useForm, type UseFormProps } from 'react-hook-form';
import type * as yup from 'yup';

export const useFormData = <L extends yup.ObjectSchema<yup.AnyObject>>(
	schema: L,
	defaultValues?: UseFormProps<FormData<L>>['defaultValues'],
	options?: IUseFormDataOptions<L>
): IUseFormData<L> => {
	const cacheKey = options?.cache?.key;
	const cacheOnRouteChange = options?.cache?.cacheOnRouteChange !== false;
	const promptOnUnload = options?.cache?.promptOnUnload !== false;
	const cacheOnUnload = options?.cache?.cacheOnUnload !== false;
	const onBeforeUnload = options?.cache?.onBeforeUnload;

	const getCachedData = (): UseFormProps<FormData<L>>['defaultValues'] => {
		if (!cacheKey) return defaultValues;
		const cachedData = sessionStorage.getItem(cacheKey);
		if (cachedData) {
			try {
				return JSON.parse(cachedData) as UseFormProps<FormData<L>>['defaultValues'];
			} catch {
				return defaultValues;
			}
		}
		return defaultValues;
	};

	const hookFormProps = useForm<FormData<L>>({
		resolver: yupResolver(schema),
		defaultValues: getCachedData(),
		mode: options?.mode,
		reValidateMode: options?.reValidateMode,
	});

	const {
		formState: { isDirty, isValid: isRHFValid, errors, touchedFields },
		getValues,
	} = hookFormProps;

	useEffect(
		() => () => {
			if (!cacheOnRouteChange || !cacheKey) return;

			const values = getValues();

			const filteredValues = cleanFormValues(values);
			sessionStorage.setItem(cacheKey, JSON.stringify(filteredValues));
		},
		[cacheKey, cacheOnRouteChange, cacheOnUnload, getValues]
	);

	useEffect(() => {
		if (!onBeforeUnload || !isDirty) return;

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			onBeforeUnload();

			if (cacheKey && cacheOnUnload) {
				sessionStorage.setItem(cacheKey, JSON.stringify(cleanFormValues(getValues())));
			}
			if (promptOnUnload) {
				event.preventDefault();
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [isDirty, cacheKey, getValues, promptOnUnload, cacheOnUnload, onBeforeUnload]);

	const setValueAndValidate = useCallback(
		async <TFieldPath extends FieldPath<FormData<L>>>(fieldPath: TFieldPath, value: PathValue<FormData<L>, TFieldPath>) => {
			hookFormProps.setValue(fieldPath, value, {
				shouldValidate: false,
				shouldDirty: true,
				shouldTouch: true,
			});

			await hookFormProps.trigger(fieldPath);
		},
		[hookFormProps]
	);

	const getFieldError = useCallback(
		(fieldPath: FieldPath<yup.InferType<L>>) => {
			const error = get(errors, fieldPath) as FieldError | undefined;
			const touched = get(touchedFields, fieldPath) as boolean | undefined;

			return touched && error ? { ...error, message: capitalizeFirstLetter(error.message) } : undefined;
		},
		[errors, touchedFields]
	);

	const clearCache = useCallback(() => {
		if (cacheKey) {
			sessionStorage.removeItem(cacheKey);
		}
	}, [cacheKey]);

	return {
		hookFormProps,
		getFieldError,
		isFormCompleted: isRHFValid,
		setValueAndValidate,
		clearCache,
	};
};
